import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { AfterAuth, AppUI, LoadingState } from '@seedlang/state';
import { absoluteCenter, fixedCenter, flexCenterColumn, smallText } from '@seedlang/style_mixins';
import { getMessageFromError, isPresent } from '@seedlang/utils';
import Button from 'components/button/button';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import Spinner from 'components/spinner';
import MemrevLogo from 'components/memrev_logo';
import { noop } from 'lodash';
import { computed, observable, when } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import CheckoutRegistrationStep from './checkout_registration_step';
import AuthenticateConfirm from 'components/authenticate/authenticate_confirm';
import eventParamsHelper from 'utils/event_params_helper';
import withRouteParams from 'utils/with_route_params';

const Wrapper = styled.div`
  ${flexCenterColumn()}
  ${smallText()}
  ${fixedCenter()}
  background: ${props => props.background};
  z-index: ${Theme.z.foreground};
`;

const Title = styled.div`
  display: block;
  font-size: 20px;
  line-height: 24px;
  font-weight: bold;
  text-align: center;
  color: #000;
  margin: ${props => props.margin};
  word-break: break-word;
`;

const Content = styled.div`
  &.centered-vertically {
    ${flexCenterColumn()}
  }
  ${absoluteCenter()}
  background: ${props => props.background};
  border-radius: ${props => props.isMobile ? 0 : '20px'};
  max-width: ${props => props.isMobile ? '100%' : '500px'};
  max-height: ${props => props.maxHeight};
  height: ${props => props.isMobile ? '100%' : 'auto'};
  margin: ${props => props.isMobile ? '0' : '60px auto'};
  width: 100%;
  z-index: 4;
  padding: 20px;
  overflow: auto;
`;

const InnerContent = styled.div`
  ${flexCenterColumn()}
  background: #FFF;
  border-radius: 10px;
  margin-top: 0px;
`;

const PaymentWrapper = styled.div`
  ${flexCenterColumn()}
  width: 100%;
  padding: 0 20px;
  background: #FFF;
  border-radius: 5px;
`;

const LogoWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0px 0 20px 0;
  .img {
    width: 190px;
  }
`;

const RedirectText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 20px 0;
  width: 400px;
  background: #dfdfdf;
  padding: 20px;
  border-radius: 10px;
  overflow-wrap: anywhere;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const timeoutPromise = ms => new Promise((_resolve, reject) => {
  return setTimeout(() => reject(new Error('Timeout')), ms);
});

const withTimeout = (ms, fn) => {
  return Promise.race([fn(), timeoutPromise(ms)]);
};

@observer
class CheckoutMemrev extends React.Component {
  @observable startedCheckoutAsUnregistered = false;
  @observable show = 'checkout';
  @observable registrationComplete = false;
  @observable isDataLoaded = false;
  @observable currentSubscription = null;
  @observable isConfirmed = false;

  redirectLoadingState = new LoadingState();

  constructor(props) {
    super(props);

    if (document.referrer.match('checkout.stripe.com') && AppUI.getCookieValue('redirected-to-stripe')) {
      // checkout.stripe.com stays as the document.referrer even when it's not the last click
      // need to protect against having a loop between memberships.index and this page
      // which would make it impossible to get to the checkout page
      AppUI.setCookieValue('redirected-to-stripe', false);
      AppUI.routeStore.routeToNamed('memberships.index');
    } else {
      if (!AppUI.currentlyViewedMembership || AppUI.currentlyViewedMembership.id !== this.props.params.membershipId) {
        AppUI.membershipStore.getShow({ ids: { membershipId: this.props.params.membershipId } });
      }
      AppUI.createEvent('checkout - payment form', { membership_id: this.props.params.membershipId });

      this.loadUser();
    }

    this.contentRef = React.createRef();
    this.innerRef = React.createRef();
  }

  adjustLayout = () => {
    const contentEl = this.contentRef.current;
    const innerEl = this.innerRef.current;

    const contentMargin = this.props.isMobile ? 0 : 60;
    const contentPadding = 20;
    const extraHeight = contentMargin * 2 + contentPadding * 2;

    if (innerEl.clientHeight + extraHeight > contentEl.clientHeight) {
      contentEl.classList.remove('centered-vertically');
    }
    else {
      contentEl.classList.add('centered-vertically');
    }
  };

  componentDidMount() {
    AppUI.createEvent('memrev - membership modal', eventParamsHelper({
      membership_id: this.props.params.membershipId,
    }));
    this.adjustLayout();
    window.addEventListener('resize', this.adjustLayout);
  }

  componentDidUpdate() {
    this.adjustLayout();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.adjustLayout);
    this.disposer && this.disposer();
    AppUI.set('currentlyViewedMembership', null);
  }

  // ---------- Data ----------

  @computed get membership() {
    return AppUI.currentlyViewedMembership || AppUI.membershipStore.showData;
  }

  async loadUser() {
    this.show = 'loading';

    await when(() => AppUI.user);

    if (AppUI.user.signedOut) {
      this.show = 'registration';
      return;
    }

    this.gotoConfirmationStep();
  }

  // ---------- Registration ----------
  afterRegistration = () => {
    AppUI.loadUser();
    this.registrationComplete = true;
    this.gotoConfirmationStep();
  }

  // ---------- Email Confirmation ----------
  onConfirmationRequired = () => {
    this.show = 'confirm_email';

    // Resume checkout after the user clicks the link in the email
    AfterAuth.set({ path: window.location.pathname, label: 'Continue to Payment' });
  }

  // ---------- Membership Change Confirmation ----------
  gotoConfirmationStep = async () => {
    this.show = 'loading';

    if (!AppUI.userSubscriptionStore.hasIndexData) {
      await AppUI.userSubscriptionStore.getIndexPromise({});
    }

    this.currentSubscription = AppUI.user.currentGroupMembershipType && 
      AppUI.userSubscriptionStore.indexData.slice().reverse().find(
        item => item.membership.membershipTypeId === AppUI.user.currentGroupMembershipType.id 
        && (item.isActive || item.isTrialing)
      );

    this.gotoCheckoutStep();
  }

  // ---------- Redirecting to payment provider ----------
  gotoCheckoutStep = () => {
    if (this.show === 'checkout') return;

    this.show = 'checkout';

    if (!this.setViewedCheckoutCount) {
      this.setViewedCheckoutCount = true;
      AppUI.user.set('viewedCheckoutCount', AppUI.user.viewedCheckoutCount + 1);
      AppUI.authUserStore.update({ data: { viewedCheckoutCount: AppUI.user.viewedCheckoutCount } }, noop);
    }

    this.forwardToStripe();
  }

  trackFailedPayment = (message) => {
    AppUI.createEvent('checkout - failed payment', {
      membership_id: this.membership?.id,
      payment_type: 'stripe',
      message,
    });
  }

  forwardToStripe = async () => {
    const defaultErrorMessage = 'We are sorry, there seems to be an issue. Please contact us if you are unable to resolve the issue.';

    this.redirectLoadingState.started();
    AppUI.createEvent('memrev - click continue to payment', eventParamsHelper({
      membership_id: this.membership?.id || this.props.params.membershipId,
    }));
    AppUI.createEvent('checkout - click stripe button', { membership_id: this.membership?.id });

    try {
      AppUI.setCookieValue('redirected-to-stripe', true);

      await withTimeout(5000,
        async () => {
          await when(() => isPresent(this.membership?.id));

          if (this.currentSubscription) {
            window.location.href = await AppUI.stripeStore.getCustomerPortalUrl({
              action_type: 'subscription_update_confirm',
              stripe_account: this.currentSubscription.stripeAccount,
              stripe_customer_id: this.currentSubscription.stripeCustomerId,
              stripe_subscription_id: this.currentSubscription.stripeSubscriptionId,
              target_membership_id: this.membership.id,
            });
            return;
          }

          const resp = await AppUI.stripeStore.createSessionPromise({
            data: {
              quantity: 1,
              membership_id: this.membership.id,
              currency: AppUI.user?.currency,
            },
          });

          window.location = resp.url;
        },
      );

      // this.redirectLoadingState never succeeds because we expect to be redirected
    } catch (error) {
      if (error.status === 401) {
        this.redirectLoadingState.clear();
        this.show = 'registration';
        return;
      }
      console.error('Error forwarding to Stripe:', error, getMessageFromError(error));
      const message = getMessageFromError(error);
      this.trackFailedPayment(message);
      this.redirectLoadingState.failed(`${defaultErrorMessage}${message ? ` (${message})` : ''}`);
    }
  }

  // ---------- Presentation ----------

  @computed get contentMaxHeight() {
    if (AppUI.layout.isMobile) {
      return '100%';
    }
    return AppUI.showPatreonButton ? '720px' : '680px';
  }

  @computed get backgroundColor() {
    return isPresent(AppUI.site?.footerBackgroundColor) ? AppUI.site?.footerBackgroundColor : '#044067';
  }

  render() {
    return (
      <Wrapper
        className='Checkout__Wrapper'
        background={this.backgroundColor}
        isMobile={AppUI.layout.isMobile}
      >
        {
          this.membership &&
          <Content
            className='Checkout__Content'
            background='#FFF'
            maxHeight={this.contentMaxHeight}
            isMobile={AppUI.layout.isMobile}
            ref={this.contentRef}
          >
            <InnerContent className='Checkount__InnerContent' ref={this.innerRef}>
              {
                isPresent(AppUI.site?.image) &&
                <LogoWrapper>
                  <MemrevLogo />
                </LogoWrapper>
              }

              {
                this.show !== 'loading' && this.show !== 'confirm_email' &&
                <Title
                  margin='0 0 10px 0'
                >
                  {this.currentSubscription ? 'Changing your Subscription' : 'Become a Member'}
                </Title>
              }

              {
                this.show === 'loading' &&
                <Spinner />
              }

              {
                this.show === 'registration' &&
                <CheckoutRegistrationStep afterRegistration={this.afterRegistration} onConfirmationRequired={this.onConfirmationRequired} />
              }

              {
                this.show === 'confirm_email' &&
                <AuthenticateConfirm />
              }

              {
                this.show === 'checkout' &&
                <PaymentWrapper>
                  {
                    this.redirectLoadingState.errorMessage &&
                    <RedirectText>
                      {this.redirectLoadingState.errorMessage}

                      <Button
                        margin='10px 0 0 0'
                        onClick={() => AppUI.routeStore.routeToNamed('contact')}
                      >
                        Contact Us
                      </Button>
                    </RedirectText>
                  }
                  {
                    !this.redirectLoadingState.errorMessage &&
                    <Section>
                      <Spinner />
                    </Section>
                  }
                </PaymentWrapper>
              }

            </InnerContent>
          </Content>
        }

      </Wrapper>
    );
  }

}

export default withRouteParams(ErrorBoundary(CheckoutMemrev));
